<template>
  <collapse-item
    :name="productGroupIndex"
    :qaid="`bulkProductGrid${productGroupIndex}`"
    :is-enable-transition="false"
    @state-changed="stateChanged"
  >
    <template slot="title">
      <label
        v-if="group.meta.type === 'swatch'"
        class="flex flex-grow items-center"
        qaid="sku-grid.color"
        style="--color-selector-outer-dimensions: 1.25rem;"
      >
        <swatch :color="groupDisplayColor" :is-small="true" class="mr-1" />
        <span class="cursor-pointer text-gray-70">{{ groupDisplayName }}</span>
      </label>
      <div v-else class="text-secondary">
        {{ groupDisplayName }}
      </div>
    </template>

    <div class="md:flex">
      <div class="b2b-card product-card md:w-24" :class="{'lg:hidden xxl:flex': isPlp}">
        <div class="b2b-card__img w-24 md:w-auto">
          <img :src="groupDisplayImage" class="w-20 h-auto lazyload" qaid="sku-grid.image" alt="" />
        </div>
        <div class="b2b-card__body p-0 pt-1">
          <p
            v-if="isBike && productGroup.marketingModelYear && !productGroup.modelYearCurrent"
            class="text-md text-gray-80 font-medium m-0"
            qaid="sku-grid.model-year"
          >
            {{ productGroup.marketingModelYear }}
          </p>

          <p
            v-if="isBike && productGroup.modelYearCurrent"
            class="text-md text-gray-80 font-medium m-0"
            qaid="sku-grid.model-year"
          >
            {{ productGroup.modelYearCurrent }}
          </p>

          <p class="font-medium text-lg mb-0 mt-25 leading-tight" qaid="sku-grid.item-name">
            {{ productGroup.name }}
          </p>

          <!-- Variants -->
          <label
            v-if="group.meta.type === 'swatch'"
            class="flex flex-grow md:mb-1 mt-25 items-center md:block"
            qaid="sku-grid.color"
            style="--color-selector-outer-dimensions: 1.25rem;"
          >
            <swatch :color="groupDisplayColor" :is-small="true" />
            <span class="text-gray-70 leading-tight">{{ groupDisplayName }}</span>
          </label>

          <div v-else class="text-secondary">
            {{ groupDisplayName }}
          </div>
        </div>
      </div>

      <!-- Tabs (SKU and Apple Labels) -->
      <div class="md:flex-1 md:ml-2" :class="{'lg:ml-0 xxl:ml-2': isPlp}">
        <bulk-product-grid-tabs
          :base-properties="baseProperties"
          :hide-unavailable="hideUnavailable"
          :product-group="productGroup"
          :base-product="baseProduct"
          :product-group-index="productGroupIndex"
          :product-type="productType"
          :apple-table-headers="appleTableHeaders"
          :is-plp="isPlp"
          :is-pdp="isPdp"
          :is-custom-product="isCustomProduct"
          @update-stock-watch-state="(payload) => $emit('update-stock-watch-state', payload)"
          @update-cart-or-list-column="emitUpdateColumns"
        />
      </div>
    </div>
  </collapse-item>
</template>

<script>
import get from 'lodash/get';
import BulkProductGridTabs from './BulkProductGridTabs';
import {mapMutations} from 'vuex';
import CollapseItem from '@/components/CollapseItem';
import Swatch from '@/components/Swatch';

export default {
  components: {BulkProductGridTabs, CollapseItem, Swatch},

  props: {
    appleTableHeaders: {
      type: Array,
      default: () => [],
    },
    baseProperties: {
      type: Array,
      default: () => [],
    },
    hideUnavailable: Boolean,
    productGroup: {
      type: Object,
      default: () => {},
    },
    productGroupIndex: {
      type: Number,
      default: 0,
    },
    baseProduct: {
      type: String,
      default: null,
    },
    imageFormat: {
      type: String,
      default: 'png8-alpha',
    },
    imageUnsharpMask: {
      type: String,
      default: '0,0,0,0',
    },
    imageQuality: {
      type: String,
      default: '80,1',
    },
    imageSize: {
      type: Object,
      default: () => {
        return {width: 160, height: 120};
      },
    },
    productType: {
      type: String,
      validator: function(value) {
        // 'am' stands for 'after market' and represents parts/accessories
        return ['bike', 'am'].indexOf(value) !== -1;
      },
      default: null,
    },
    isPdp: {
      type: Boolean,
      default: false,
    },

    isPlp: {
      type: Boolean,
      default: false,
    },
    isCustomProduct: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      collapseStates: {},
    };
  },

  computed: {
    group() {
      return this.productGroup.group;
    },

    isBike() {
      return this.productType === 'bike';
    },

    groupDisplayImage: function() {
      const urlParams = [];

      urlParams.push(`wid=${this.imageSize.width}`);
      urlParams.push(`hei=${this.imageSize.height}`);
      urlParams.push(`fmt=${this.imageFormat}`);
      urlParams.push(`qlt=${this.imageQuality}`);

      if (this.imageUnsharpMask) {
        urlParams.push(`op_usm=${this.imageUnsharpMask}`);
      }

      const imageSuffix = `?${urlParams.join('&')}&iccEmbed=0&cache=on,on`;
      return this.group ? ACC.scene7ProductImageUrl + this.group.image + imageSuffix : null;
    },

    groupDisplayColor() {
      return get(this.group, 'value.options.colorSwatchName');
    },

    groupDisplayName() {
      return get(this.group, 'value.options.displayName');
    },
  },

  methods: {
    stateChanged(value) {
      let skuList = this.productGroup.products.map((obj) => obj.sku);

      for (const sku of skuList) {
        this.collapseStates[sku] = value;
      }
      this.updateCollapseState(this.collapseStates);
    },

    emitUpdateColumns(resData) {
      this.$emit('update-cart-or-list-column', resData);
    },
    ...mapMutations('skuGrid', ['updateCollapseState']),
  },
};
</script>

<style scoped>
.product-card {
  min-width: 6rem;
}
</style>
